// What the subscription cannot be billed without, and what each field has to
// look like once it holds anything.
//
// Built the same way the brand's rules are — a rule per field, next to the
// normaliser that feeds it — and leaning on `lib/fieldFormat` for the three
// questions every form in the app asks the same way: an email, an Indian
// mobile number, a pincode. The only rule that is this screen's own is the
// GSTIN, because nothing else in the app collects one.

import {
  emailError,
  mobileError,
  pincodeError,
} from "../../lib/fieldFormat";
import type { BillingDetails } from "./types/billing.types";

/**
 * What an invoice cannot be raised without: who it is to, how to reach them,
 * and where they are.
 *
 * All of it except the GSTIN. Every field in this list wears a red asterisk on
 * the form — the app's own mark for a required field, the same one `My brand`
 * and `Add client` use — and the GSTIN wears nothing rather than the
 * "(optional)" it used to carry: with the other seven marked, the unmarked one
 * is the optional one, and saying it twice is the same fact in two notations.
 * This list is what actually decides; the asterisks only have to agree with it.
 */
export const REQUIRED_FIELDS = [
  "fullName",
  "email",
  "phone",
  "address",
  "city",
  "state",
  "pincode",
] as const;

/**
 * Optional, but checked once something has been typed: a GSTIN is claimed in
 * order to be credited for the tax, and one that is nearly right buys nothing
 * — it fails at the far end, months later, with no way back to this form.
 */
export const OPTIONAL_VALIDATED_FIELDS = ["gstin"] as const;

export type RequiredField = (typeof REQUIRED_FIELDS)[number];
export type OptionalValidatedField = (typeof OPTIONAL_VALIDATED_FIELDS)[number];
export type ValidatedField = RequiredField | OptionalValidatedField;

export const VALIDATED_FIELDS = [
  ...REQUIRED_FIELDS,
  ...OPTIONAL_VALIDATED_FIELDS,
] as const;

export type BillingErrors = Partial<Record<ValidatedField, string>>;

/** Names used in "<field> is required", so the message reads like the label. */
const LABELS: Record<ValidatedField, string> = {
  fullName: "Full name",
  email: "Email",
  phone: "Phone number",
  address: "Street address",
  city: "City",
  state: "State",
  pincode: "ZIP/Postal code",
  gstin: "GSTIN",
};

/**
 * A GSTIN: two digits of state code, the ten characters of the holder's PAN,
 * a registration number, a literal `Z`, and a check character.
 *
 * The `Z` is what makes this worth checking at all — it is in the same place
 * in every GSTIN ever issued, so a number typed one character short fails here
 * rather than at the tax department.
 */
const GSTIN_PATTERN = /^\d{2}[A-Z]{5}\d{4}[A-Z][A-Z\d]Z[A-Z\d]$/;

const isRequired = (field: ValidatedField): field is RequiredField =>
  (REQUIRED_FIELDS as readonly string[]).includes(field);

/** Empty string means the field is fine. */
export const fieldError = (field: ValidatedField, raw: string): string => {
  const value = raw.trim();

  // An empty optional field has nothing to be wrong about.
  if (!value) return isRequired(field) ? `${LABELS[field]} is required` : "";

  switch (field) {
    case "email":
      return emailError(value);
    case "phone":
      return mobileError(LABELS[field], value);
    case "pincode":
      return pincodeError(value);
    // Somewhere a courier could find: enough that a street number and a road
    // have been given, rather than a single character to get past the field.
    case "address":
      return value.length >= 5
        ? ""
        : "Enter the street address the invoice should carry";
    case "gstin":
      return GSTIN_PATTERN.test(value) ? "" : "Enter a valid 15-character GSTIN";
    default:
      return "";
  }
};

/** Only failing fields appear, so an empty object means the form is valid. */
export const validateBillingDetails = (
  details: BillingDetails
): BillingErrors => {
  const errors: BillingErrors = {};

  VALIDATED_FIELDS.forEach((field) => {
    const message = fieldError(field, details[field]);
    if (message) errors[field] = message;
  });

  return errors;
};

/**
 * A UPI address — `ravi.sharma@okhdfcbank`, `9876543210@ybl`. The same rule
 * the brand form's UPI ID answers to, and here for the same reason: the
 * handle after the `@` is a bank's, and those are letters only — unlike an
 * email there is no dot and no TLD in it, which is exactly the mistake this
 * catches.
 *
 * Its own copy rather than an import from the brand's validation: that one is
 * about where a dietitian is *paid*, this is where a mandate request is
 * *sent*, and one moving is no reason for the other to.
 */
const VPA_PATTERN = /^[a-z0-9][a-z0-9.\-_]{1,}@[a-z]{2,}$/;

/** Empty string means the address is fine. Empty input is a missing answer
 *  rather than a malformed one, and says so. */
export const vpaError = (raw: string): string => {
  const value = raw.trim();
  if (!value) return "Enter the UPI ID the mandate should be sent to";
  return VPA_PATTERN.test(value) ? "" : "Enter a valid UPI ID";
};

/**
 * A GSTIN is printed, quoted and stored in upper case, and holds nothing but
 * letters and digits — so one pasted out of a document with its spaces still
 * attached lands clean.
 */
export const normaliseGstinInput = (raw: string): string =>
  raw
    .replace(/[^A-Za-z0-9]/g, "")
    .toUpperCase()
    .slice(0, 15);
